'use client'

import { ChevronLeftIcon, ChevronRightIcon } from '../../icons'
import {
    ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS,
    resolveIconButtonAriaLabel,
} from '../../../constants/iconButtonAriaLabelMessageKeys'
import React, { FC, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { TextTypes } from '../../../types'
import { Button, ButtonVariants, DictionaryText, Text } from '../../atoms'
import { SelectOption } from '../../../types/SelectInput'
import { SingleSelectInput } from '../../FormElements'
import { DictionaryContext, PaginationContext } from '../../../providers'

interface Props {
    initialPerPage?: number
    perPageOptions: SelectOption[]
    itemsPerPageLabel: ReactNode
    xOfTotalItemsLabelKey: string
    ofTotalPagesLabelKey: string
    /** Placeholder that will be replaced. It always replaces including {{}}, so if "amount" is provided it will replace "{{ amount }}" */
    labelParamPlaceholder?: string
}

export const Pagination: FC<Props> = ({
    initialPerPage = 25,
    perPageOptions,
    itemsPerPageLabel,
    xOfTotalItemsLabelKey,
    ofTotalPagesLabelKey,
    labelParamPlaceholder = 'amount',
}: Props) => {
    const dictionary = useContext(DictionaryContext)
    const { params, totalResults, initialPage, paginateHandler, currentPage, setCurrentPage } =
        useContext(PaginationContext)
    // Seeded from the provider so a per-page value restored from the URL is kept.
    const [perPage, setPerPage] = useState<number>(params?.limit ?? initialPerPage)
    const [totalItemsToShowInLabel, setTotalItemsToShowInLabel] = useState<number>(+currentPage * +perPage)
    const RANDOM_ID = String(Math.round(Math.random() * 999999))

    // Derived rather than state: this component is often mounted only once results exist, so state
    // would still hold its initial value on the first render and clamp a restored page back to 1.
    const totalPages = useMemo(() => Math.max(1, Math.ceil(totalResults / +perPage)), [totalResults, perPage])
    const pageNumbers = useMemo<SelectOption[]>(
        () =>
            Array.from(Array(totalPages).keys()).map<SelectOption>((key) => ({
                value: key + 1,
                label: (key + 1).toString(),
            })),
        [totalPages],
    )

    useEffect(() => {
        setTotalItemsToShowInLabel(getTotalItemsInView())
    }, [paginateHandler])

    useEffect(() => {
        if (+currentPage > totalPages) return
        debouncedFetchData()
    }, [currentPage, perPage])

    useEffect(() => {
        // Only clamp once results are known, otherwise a page restored from the URL is reset
        // before the first response arrives.
        if (totalResults > 0 && +currentPage > totalPages) setCurrentPage(1)
    }, [totalPages, totalResults])

    const debouncedFetchData = useDebouncedCallback(() => {
        const offset = +currentPage * +perPage - +perPage
        const limit = +perPage
        paginateHandler(offset, limit)
    }, 250)

    const getTotalItemsInView = (): number => {
        const isOnePager: boolean = totalResults <= perPage
        return isOnePager ? totalResults : +currentPage * +perPage
    }

    const getTotalPageNumber = (): number => (+currentPage - 1) * +perPage + 1

    const perPageHandler = (option: SelectOption): void => {
        setPerPage(option.value ? +option.value : initialPerPage)
        setCurrentPage(1)
    }
    const pageNumberHandler = (option: SelectOption): void => setCurrentPage(option.value ? +option.value : initialPage)

    const previousPageHandler = (): void => {
        if (+currentPage <= 1) return
        const previousPage: number = +currentPage - 1
        setCurrentPage(previousPage)
    }

    const nextPageHandler = (): void => {
        if (+currentPage >= totalPages) return
        const nextPage: number = +currentPage + 1
        setCurrentPage(nextPage)
    }

    return (
        <>
            <div className="flex justify-between w-full bg-shade-100 py-3" data-testid="pagination">
                <div className="inline-flex items-center gap-2 text-text-default">
                    <Text type={TextTypes.PARAGRAPH}>{itemsPerPageLabel}:</Text>
                    <SingleSelectInput
                        id={`${RANDOM_ID}_pagination-per-page-menu`}
                        options={perPageOptions}
                        value={perPage}
                        defaultValue={perPage}
                        onChange={perPageHandler}
                        data-testid="pagination-per-page-select"
                    />
                    <Text type={TextTypes.PARAGRAPH}>
                        {getTotalPageNumber()} - {totalItemsToShowInLabel}{' '}
                        <DictionaryText
                            textKey={xOfTotalItemsLabelKey}
                            replacer={{ [labelParamPlaceholder]: totalResults.toString() }}
                        />
                    </Text>
                </div>
                <div className="inline-flex items-center gap-2 text-text-default">
                    <SingleSelectInput
                        id={`${RANDOM_ID}_pagination-page-menu`}
                        disabled={totalPages <= 1}
                        key={`page-selection-${totalPages}`}
                        options={pageNumbers}
                        value={currentPage}
                        defaultValue={currentPage}
                        isSearchable={true}
                        onChange={pageNumberHandler}
                        data-testid="pagination-page-select"
                    />
                    <Text type={TextTypes.PARAGRAPH}>
                        <DictionaryText
                            textKey={ofTotalPagesLabelKey}
                            replacer={{ [labelParamPlaceholder]: totalPages.toString() }}
                        />
                    </Text>
                    <Button
                        icon={<ChevronLeftIcon />}
                        disabled={+currentPage === 1}
                        variant={ButtonVariants.QUIET}
                        onClick={() => previousPageHandler()}
                        data-testid="pagination-previous-button"
                        aria-label={resolveIconButtonAriaLabel(
                            dictionary,
                            ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS.PAGINATION_PREVIOUS,
                        )}
                    />
                    <Button
                        icon={<ChevronRightIcon />}
                        disabled={+currentPage === totalPages}
                        variant={ButtonVariants.QUIET}
                        onClick={() => nextPageHandler()}
                        data-testid="pagination-next-button"
                        aria-label={resolveIconButtonAriaLabel(
                            dictionary,
                            ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS.PAGINATION_NEXT,
                        )}
                    />
                </div>
            </div>
        </>
    )
}
